// parse.ts — a deliberately tiny Markdown subset parser for assistant chat messages, which are
// LLM-generated and therefore UNTRUSTED content. Originally lived inline in
// `apps/widget/src/markdown.ts` as a DOM-building walk; extracted here so the SAME parsing logic
// (and, critically, the SAME link-scheme XSS gate) backs both the widget's DOM renderer and
// `apps/web`'s React renderer for the public playground, rather than two independently
// maintained — and independently driftable — copies of this logic.
//
// This module never touches a DOM API. It returns a plain tree (`tree.ts`) that a renderer walks
// to build real nodes (`createElement`/`createTextNode` on the widget side, real JSX elements on
// the React side — never an HTML-string step either renderer could get wrong). That property is
// enforced in each renderer, not here; this file just decides structure.
//
// Supported subset only (originally spec D11/M4 task-4, unchanged by this extraction):
//   **bold**                      → StrongNode
//   [text](url)                   → LinkNode, http/https only — any other scheme (javascript:,
//                                    data:, etc.) is left as the literal source text, never a link
//   `- ` at the start of a line   → a ListBlock item (one list per contiguous run of lines)
//   single \n                     → BreakNode
//   \n\n (or more)                → block break (a new element of the returned array)
// Nothing else is special: literal `<`, `&`, `"` etc. all land in TextNode values, which every
// consuming renderer must treat as plain text (never HTML-parsed) — there is no HTML-string step
// for anything to sneak through.
import type { Block, InlineLeaf, InlineNode } from './tree';
import { LINK_SCHEME } from './tree';

// Matches **bold** (non-greedy, no literal `*` inside so "**a** and **b**" doesn't span both) or
// [text](url) (no literal `]`/`)` inside their respective halves). Neither branch can loop
// forever on malformed input (unclosed `**`/`[`/`(`) — a non-match just leaves the raw characters
// to fall through as plain text on the next scan position.
const INLINE_TOKEN = /\*\*([^*]+?)\*\*|\[([^\]]*)\]\(([^)]*)\)/g;

/** Splits `text` on `\n` into `TextNode`/`BreakNode` leaves (single-newline rule). Skips
 * zero-length segments (e.g. two blank lines in a row) so no stray empty text nodes accumulate —
 * this is the ONE place that decision is made, so every renderer downstream sees the same
 * already-normalized leaf list. */
function splitBreaks(text: string): InlineLeaf[] {
  const parts = text.split('\n');
  const leaves: InlineLeaf[] = [];
  parts.forEach((part, index) => {
    if (index > 0) leaves.push({ type: 'break' });
    if (part.length > 0) leaves.push({ type: 'text', value: part });
  });
  return leaves;
}

/** Parses `**bold**` and `[text](url)` inline tokens out of `str` and returns the resulting
 * node list, in order. Plain-text runs between/around tokens go through `splitBreaks` so
 * newline-to-break conversion still applies inside list items and paragraphs alike. */
function parseInline(str: string): InlineNode[] {
  INLINE_TOKEN.lastIndex = 0;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const nodes: InlineNode[] = [];

  while ((match = INLINE_TOKEN.exec(str)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...splitBreaks(str.slice(lastIndex, match.index)));
    }

    const [full, boldText, linkText, linkUrl] = match;
    if (boldText !== undefined) {
      nodes.push({ type: 'strong', children: splitBreaks(boldText) });
    } else if (LINK_SCHEME.test((linkUrl ?? '').trim())) {
      nodes.push({ type: 'link', href: linkUrl ?? '', children: splitBreaks(linkText ?? '') });
    } else {
      // Disallowed scheme (javascript:, data:, vbscript:, empty, malformed, ...) — never emit a
      // LinkNode for it. Fall back to the raw matched source as plain text so the visitor still
      // sees *something* recognizable rather than silently dropped content.
      nodes.push(...splitBreaks(full));
    }

    lastIndex = INLINE_TOKEN.lastIndex;
  }

  if (lastIndex < str.length) {
    nodes.push(...splitBreaks(str.slice(lastIndex)));
  }

  return nodes;
}

/** A block is a bullet list only if every non-blank line starts with `- `. Mixed content (some
 * bullet lines, some prose) is treated as an ordinary paragraph instead — safer default than
 * guessing which lines are "really" list items. */
function isBulletBlock(nonBlankLines: string[]): boolean {
  return nonBlankLines.length > 0 && nonBlankLines.every((line) => line.startsWith('- '));
}

/** Parses a constrained Markdown subset (bold, http/https links, `- ` bullet lists, single/double
 * newlines) into a `Block[]` tree — see the module comment for the full rule set and the XSS
 * rationale for the link-scheme gate. Pure and DOM-free: safe to call from a widget bundle, a
 * server, or a React render. */
export function parseMarkdown(text: string): Block[] {
  if (text.length === 0) return [];

  const blocks: Block[] = [];
  const rawBlocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/);

  for (const block of rawBlocks) {
    if (block.trim().length === 0) continue;

    const lines = block.split('\n');
    const nonBlankLines = lines.map((line) => line.trim()).filter((line) => line.length > 0);

    if (isBulletBlock(nonBlankLines)) {
      blocks.push({
        type: 'list',
        items: nonBlankLines.map((line) => parseInline(line.slice(2))),
      });
    } else {
      blocks.push({ type: 'paragraph', children: parseInline(block) });
    }
  }

  return blocks;
}
